import React from 'react';
import { Modal } from '../ui/Modal';
import { Calendar, DollarSign, MapPin, Building2, FileText, Shield, Link, Download } from 'lucide-react';
import type { ContractResponse } from '../../lib/api/samGov';
import { toast } from 'react-hot-toast';

interface ContractDetailsModalProps {
  contract: ContractResponse;
  isOpen: boolean;
  onClose: () => void;
}

export function ContractDetailsModal({ contract, isOpen, onClose }: ContractDetailsModalProps) {
  const handleDownload = async () => {
    try {
      // In a real app, this would download the contract documents
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Contract documents downloaded successfully');
    } catch (error) {
      toast.error('Failed to download contract documents');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Contract Details"
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{contract.title}</h2>
          <div className="mt-2 flex items-center text-sm text-gray-500">
            <MapPin className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>{contract.location || 'Location not specified'}</span>
          </div>
        </div>

        {/* Key Details */}
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center">
              <Building2 className="mr-2 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Agency</p>
                <p className="text-sm text-gray-900">{contract.agency}</p>
              </div>
            </div>
            <div className="flex items-center">
              <DollarSign className="mr-2 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Value</p>
                <p className="text-sm text-gray-900">
                  {contract.value
                    ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(contract.value)
                    : 'Not specified'}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="mr-2 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Deadline</p>
                <p className="text-sm text-gray-900">
                  {new Date(contract.deadlineDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <Shield className="mr-2 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Set-Aside</p>
                <p className="text-sm text-gray-900">{contract.setAside || 'None'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="mr-2 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">NAICS Code</p>
                <p className="text-sm text-gray-900">{contract.naicsCode}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <h3 className="text-sm font-medium text-gray-900">Description</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-500">
            {contract.description}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <a
            href={contract.contractUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Link className="mr-2 h-4 w-4" />
            View on SAM.gov
          </a>
          <button
            onClick={handleDownload}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            <Download className="mr-2 h-4 w-4" />
            Download Documents
          </button>
        </div>
      </div>
    </Modal>
  );
}